<template>
  <CookieModal />
  <div
    class="contentContainer grow"
    :style="{ 'margin-bottom': `${bottomMargin + 10}px` }"
  >
    <div class="title-area">
      <h1 class="h bold mb-1">{{ $t("bnpl:limit.login.title.text") }}</h1>
      <p class="has-text-grey">
        {{ $t("bnpl:limit.login.subtitle") }}
      </p>
    </div>
    <div class="main-area flex-column gap-14 mt-1">
      <p class="bold has-text-size-14">
        {{ $t("bnpl:limit.login.cards.title") }}
      </p>
      <div class="info-cards mb-1">
        <USPCard
          :icon="$t('bnpl:limit.login.card1.icon')"
          :title="$t('bnpl:limit.login.card1.title')"
          :description="$t('bnpl:limit.login.card1.description')"
        />
        <USPCard
          :icon="$t('bnpl:limit.login.card2.icon')"
          :title="$t('bnpl:limit.login.card2.title')"
          :description="$t('bnpl:limit.login.card2.description')"
        />
      </div>
      <p class="bold has-text-size-14">
        {{ $t("bnpl:limit.login.reviews.title") }}
      </p>
      <div class="whitebox review-box p-2 mb-1">
        <StarRatingWithAvg :score="averageScore" :reviewCount="reviewCount" />
        <div
          class="flex1 customer-review"
          @mouseenter="setScroll(false)"
          @mouseleave="setScroll(true)"
        >
          <CustomerReview
            v-if="featuredReviews[currentReview]"
            :review="featuredReviews[currentReview]"
          />
        </div>
      </div>
    </div>
    <div class="login-area hide-on-mobile">
      <div class="whitebox-shadowed px-2 py-3 gap-8">
        <PaymentLogin />
        <p class="has-text-size-10 has-text-grey">
          {{ $t('bnpl:limit.welcome.login.terms_of_use') }}
          <a class="link" :href="$t('bnpl:limit.welcome.login.link.href')" target="_blank">{{ $t('bnpl:limit.welcome.login.link.text') }}</a>
        </p>
      </div>
    </div>
  </div>
  <div
    class="mobile-fixed hide-on-desktop whitebox-shadowed p-3 gap18"
    ref="mobileLogin"
  >
    <p class="mb-2">
      {{ $t("bnpl:login.help_text") }}
    </p>
    <PaymentLogin />
  </div>
</template>

<script setup>
import { useCartStore } from "~/stores/cart";
import { useReviewStore } from "~/stores/review";
import { useCountryStore } from "~~/stores/country";

const cartStore = useCartStore();
const reviewStore = useReviewStore();
const countryStore = useCountryStore();

const cart = computed(() => cartStore.cart);

const mobileLogin = ref(null);

const currentReview = ref(0);
const maxReviews = computed(() => Math.min(featuredReviews.value.length, 3));
const isScrollStopped = ref(false);

const setScroll = (value) => {
  isScrollStopped.value = !value;
};

const averageScore = computed(() => {
  const scores = reviewStore.reviewSummary.scoreSummary;
  let count = 0;
  let sum = 0;
  for (const score in scores) {
    count += scores[score];
    sum += scores[score] * parseInt(score);
  }
  if (sum / count) {
    return sum / count;
  } else {
    return 0;
  }
});
const reviewCount = computed(() => {
  const scores = reviewStore.reviewSummary.scoreSummary;
  let count = 0;
  for (const score in scores) {
    count += scores[score];
  }
  if (count) {
    return count;
  } else {
    return 0;
  }
});

const featuredReviews = computed(() => {
  return reviewStore.featuredReviews;
});

const bottomMargin = ref(0);

let reviewScrollInterval = null;
const onResize = () => {
  if (mobileLogin.value) {
    bottomMargin.value = mobileLogin.value.offsetHeight;
  }
};

onMounted(function () {
  try {
    reviewStore.fetchReviewSummary();
    reviewStore.fetchFeaturedReviews(countryStore.countryId);
  } catch (err) {
    console.error(err);
  }
  startReviewScroll();
  bottomMargin.value = mobileLogin.value.offsetHeight;
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  clearInterval(reviewScrollInterval);
  window.removeEventListener("resize", onResize);
});

const startReviewScroll = () => {
  reviewScrollInterval = setInterval(() => {
    if (!isScrollStopped.value) {
      if (currentReview.value + 1 >= maxReviews.value) {
        currentReview.value = 0;
      } else {
        ++currentReview.value;
      }
    }
  }, 4000);
};
</script>

<style scoped lang="scss">
.contentContainer {
  position: relative;
  display: grid;
  grid-template-areas: "title empty" "main login";
  grid-template-rows: min-content auto;
  grid-template-columns: auto min-content;
  align-items: start;
  justify-items: stretch;
  max-width: 1000px;
  gap: 32px;
}

.main-area {
  grid-area: main;
}

.login-area {
  grid-area: login;
  position: sticky;
  top: 92px;
  width: 343px;
}

.title-area {
  grid-area: title;
  align-self: stretch;
}

.review-box {
  display: flex;
  flex-direction: row;
  gap: 12px;
  height: 175px;
}

.whitebox-shadowed {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.02);
}

.info-cards {
  display: flex;
  flex-direction: row;
  gap: 14px;
}

.mobile-fixed {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: 0px -4px 20px 0px rgba(0, 0, 0, 0.1);
}

@media only screen and (max-width: 792px) {
  .contentContainer {
    grid-template-areas: "title" "main";
    grid-template-rows: min-content auto;
    grid-template-columns: auto;
  }

  .info-cards {
    flex-direction: column;
  }

  .hide-on-mobile {
    display: none;
  }

  .review-box {
    flex-direction: column;
    gap: 16px;
    height: 250px;

    .customer-review {
      flex: 100;
    }
  }
}

@media only screen and (min-width: 661px) {
  .hide-on-desktop {
    display: none;
  }
}
</style>
